// SPDX-License-Identifier: Elastic-2.0
/**
 * DashboardLayout — persistent chrome wrapping the authenticated route group (T-020).
 *
 * Mounted by `routing/router.tsx` as the layout element for `/dashboard`,
 * `/routines`, `/suggestions`, `/settings`. Renders:
 *
 *   ┌──────────────┬──────────────────────────────────────────┐
 *   │              │  TopBar  (menu · wordmark · Theme · 🔔 · 👤)   │
 *   │   Sidebar    ├──────────────────────────────────────────┤
 *   │  (264 / 64)  │                                          │
 *   │              │              <Outlet />                  │
 *   │  Account     │                                          │
 *   │  Sections    │                                          │
 *   └──────────────┴──────────────────────────────────────────┘
 *
 * Composition is intentional: this component owns the *chrome shell*
 * only — Sidebar / TopBar / Page are pure primitives. The sidebar
 * entries come from `sidebarConfig.ts` (14 items in 3 sections per the
 * F-330 IA refresh), the active state is derived from `useLocation()`,
 * and persistent UI state (sidebar collapse) lives in `useUiPrefs`.
 *
 * Mobile (< 768px): sidebar becomes an off-canvas drawer triggered by
 * the TopBar menu button. The CSS owns the actual transform; this
 * component just toggles the `sidebarOpen` class on the shell.
 *
 * F-330 (T-011):
 *   - Every sidebar item is now internal — the old external `/ha`
 *     entry was relocated to Settings → AboutSection.
 *   - Tier-gated items (`tier: "plus" | "pro"`) render a `<TierBadge>`
 *     in the `endSlot`. Sugerencias renders a `<CountBadge>` driven by
 *     `useSidebarBadgeValue("suggestionsPending")`.
 *   - `aria-label` on tier-gated items includes a "·  PLUS" / " · PRO"
 *     suffix so screen readers announce gating (TierBadge itself is
 *     `aria-hidden`).
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { GradientOrb } from "../../components/GradientOrb/GradientOrb";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { SidebarSection } from "../../components/Sidebar/SidebarSection";
import { SidebarItem } from "../../components/Sidebar/SidebarItem";
import { TopBar } from "../../components/TopBar/TopBar";
import { Icon } from "../../components/Icon/Icon";
import { MirlaiMark } from "../../components/MirlaiMark/MirlaiMark";
import { ThemePill } from "../../components/ThemePill/ThemePill";
import { TierBadge } from "../../components/TierBadge/TierBadge";
import { CountBadge } from "../../components/CountBadge/CountBadge";

import { AccountChip } from "./AccountChip";
import { ConnectionBanner } from "./ConnectionBanner";
import { UpdateBanner } from "../system/UpdateBanner";
import {
  SIDEBAR_SECTIONS,
  type SidebarItemConfig,
  type SidebarSectionConfig,
} from "./sidebarConfig";
import { useSidebarBadgeValue } from "./useSidebarBadgeValue";
import { useUiPrefs } from "./useUiPrefs";
import { useWebSocketBridge } from "./wsBridge";
import {
  useOfflineDetection,
  useOfflineQueryBridge,
} from "../../pwa/useOfflineDetection";
import { Banner } from "../../components/Banner/Banner";

import styles from "./DashboardLayout.module.css";

/**
 * Map sidebar route paths → i18n breadcrumb keys for the topbar wordmark.
 * Keys match the new F-330 IA — one entry per sidebar destination. Falls
 * back to `null` for unmatched paths (keeps the topbar chrome neutral).
 */
const BREADCRUMB_BY_PATH: Record<string, string> = {
  "/dashboard": "sidebar.home",
  "/suggestions": "sidebar.suggestions",
  "/routines": "sidebar.automations",
  "/devices": "sidebar.devices",
  "/account": "sidebar.account",
  "/plan": "sidebar.plan",
  "/remote-access": "sidebar.remote_access",
  "/backups": "sidebar.backups",
  "/voice": "sidebar.voice",
  "/homes": "sidebar.homes",
  "/api-keys": "sidebar.api_keys",
  "/marketplace": "sidebar.marketplace",
  "/publish": "sidebar.publish",
  "/analytics": "sidebar.analytics",
  "/settings": "sidebar.settings",
};

/**
 * Resolve which sidebar item is active for a given pathname. Prefix-
 * matches so a future nested route (`/settings/billing`) still lights
 * up the parent Settings entry. Every item is now internal (F-330), so
 * there's no external-link branch to short-circuit.
 */
function isItemActive(item: SidebarItemConfig, pathname: string): boolean {
  const target = item.to;
  if (target === pathname) return true;
  return pathname.startsWith(`${target}/`);
}

export function DashboardLayout(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const collapsed = useUiPrefs((s) => s.sidebarCollapsed);
  const toggleCollapsed = useUiPrefs((s) => s.toggleSidebarCollapsed);

  // F-330 (T-006): subscribes once per layout mount; per technical
  // spec § 5.3 we always call the hook (cheap — TanStack de-dupes).
  // Drives both the sidebar Sugerencias CountBadge AND the topbar bell.
  const suggestionsCount = useSidebarBadgeValue("suggestionsPending");
  const pendingSuggestions = suggestionsCount ?? 0;

  // WS → TanStack Query bridge (T-023): translate `device_state`,
  // `suggestion_new`, `suggestion_updated`, `routine_triggered` frames
  // into cache invalidations on the keys the dashboard hooks own.
  // Mounted here (not in <App />) so the bridge is alive only for
  // authenticated routes — pre-login surfaces never subscribe.
  useWebSocketBridge();

  // Offline → online query refresh (T-030). Mounted alongside the WS
  // bridge so cached data refreshes the moment the browser regains
  // connectivity. The `useOfflineDetection` read-only hook is consumed
  // separately by the banner — this one only owns the side-effect.
  useOfflineQueryBridge();
  const { offline, lastOnlineAt } = useOfflineDetection();

  // Stale-data backstop (T-023): if the tab was hidden long enough that
  // we missed WS frames (and the WS layer paused itself per T-008 § 7),
  // sweep every query on return so the cache reflects authoritative
  // state before the user notices. Cheap: TanStack only refetches keys
  // with mounted observers, so background queries stay parked.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        void queryClient.invalidateQueries();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [queryClient]);

  // Mobile drawer state — separate from desktop collapse, since on
  // mobile the sidebar is hidden by default (not collapsed; *gone*).
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = (): void => setDrawerOpen(false);

  const shellClassName = [
    styles.shell,
    collapsed ? styles.collapsed : null,
    drawerOpen ? styles.sidebarOpen : null,
  ]
    .filter(Boolean)
    .join(" ");

  /** Toggle: on desktop = collapse; on mobile = drawer open/close. */
  const handleMenuToggle = (): void => {
    if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches) {
      setDrawerOpen((open) => !open);
      return;
    }
    toggleCollapsed();
  };

  const renderItem = (item: SidebarItemConfig): JSX.Element => {
    const active = isItemActive(item, location.pathname);
    const label = t(item.label_i18n);

    // Right-aligned chip slot: tier pill OR reactive count badge OR none.
    // Tier and badgeKey are mutually exclusive in the current config, but
    // tier wins if both were ever set (visual hint dominates information).
    let endSlot: JSX.Element | null = null;
    if (item.tier) {
      endSlot = <TierBadge kind={item.tier} />;
    } else if (item.badgeKey === "suggestionsPending") {
      endSlot = (
        <CountBadge
          value={suggestionsCount}
          ariaLabel={t("sidebar.suggestions_pending_count", {
            count: suggestionsCount ?? 0,
          })}
        />
      );
    }

    // a11y suffix (functional spec § 7): screen readers should hear the
    // tier ("Remote access · PLUS") since the TierBadge itself is
    // aria-hidden. `tier_aria_suffix` is " · {{tier}}".
    const ariaLabel = item.tier
      ? `${label}${t("sidebar.tier_aria_suffix", { tier: t(`tierBadge.${item.tier}`) })}`
      : label;

    return (
      <SidebarItem
        key={item.id}
        icon={<Icon name={item.icon} size={16} />}
        label={label}
        active={active}
        endSlot={endSlot}
        aria-label={ariaLabel}
        onClick={() => {
          navigate(item.to);
          closeDrawer();
        }}
      />
    );
  };

  const renderSection = (section: SidebarSectionConfig): JSX.Element => (
    <SidebarSection key={section.title_i18n} title={t(section.title_i18n)}>
      {section.items.map(renderItem)}
    </SidebarSection>
  );

  // Breadcrumb derived from active path; fallback empty (keeps the topbar
  // chrome neutral on unmatched routes).
  const breadcrumbKey = BREADCRUMB_BY_PATH[location.pathname];
  const breadcrumb = breadcrumbKey ? t(breadcrumbKey) : null;

  return (
    <div className={shellClassName} data-testid="dashboard-layout">
      <div className={styles.sidebarSlot}>
        <Sidebar collapsed={collapsed} onCollapsedChange={toggleCollapsed}>
          {/* F-342: brand wordmark anchored at the top of the sidebar,
              above the account chip. Mirrors the design screenshot. */}
          <div className={styles.sidebarWordmark} data-testid="sidebar-wordmark">
            <MirlaiMark size={22} />
            <span>mirlai</span>
          </div>
          <AccountChip />
          {SIDEBAR_SECTIONS.map(renderSection)}
          {/* F-342: Help & support footer entry, anchored to the bottom
              of the sidebar via margin-top:auto on its wrapper. Non-routed
              (target href TBD); for now opens a stub mailto so the surface
              has a real interaction. Replace with real support route /
              modal when the surface ships. */}
          <a
            href="mailto:support@mirlai.app"
            className={styles.helpEntry}
            data-testid="sidebar-help"
          >
            <Icon name="help" size={18} />
            <span className={styles.helpLabel}>{t("account_chip.help_label")}</span>
          </a>
        </Sidebar>
      </div>

      {/* Mobile scrim: clicking dismisses the drawer. Decorative on desktop. */}
      {drawerOpen && (
        <button
          type="button"
          className={styles.scrim}
          aria-label={t("topbar.menu")}
          onClick={closeDrawer}
        />
      )}

      <div className={styles.main}>
        {/* Ambient gradient orb fills the main column background,
            mirroring the landing page hero. `full` variant = the
            same 3-layer atmosphere onboarding uses, so the visual
            language stays consistent across pre/post-auth surfaces.
            Drift animation (transform-only) is applied to all
            variants in GradientOrb.module.css. Decorative — the
            component itself is aria-hidden + pointer-events:none. */}
        <GradientOrb variant="full" />
        <TopBar
          left={
            <>
              <button
                type="button"
                className={styles.menuButton}
                onClick={handleMenuToggle}
                aria-label={t("topbar.menu")}
                aria-expanded={drawerOpen}
                data-testid="menu-toggle"
              >
                <Icon name="chevron" size={18} />
              </button>
              <span className={styles.wordmark}>
                <MirlaiMark size={22} />
                <span>mirlai</span>
                {breadcrumb && <span className={styles.breadcrumb}>· {breadcrumb}</span>}
              </span>
            </>
          }
          right={
            <>
              <ThemePill />
              <button
                type="button"
                className={styles.iconButton}
                aria-label={t("topbar.search")}
                data-testid="search-button"
              >
                <Icon name="search" size={17} />
              </button>
              <button
                type="button"
                className={`${styles.iconButton} ${styles.bellWrap}`}
                aria-label={t("topbar.notifications")}
                onClick={() => navigate("/suggestions")}
                data-testid="bell-button"
              >
                <Icon name="bell" size={17} />
                {pendingSuggestions > 0 && (
                  <span
                    className={styles.bellBadge}
                    aria-label={`${pendingSuggestions} pending`}
                    data-testid="bell-badge"
                  >
                    {pendingSuggestions}
                  </span>
                )}
              </button>
              <AccountChip compact />
            </>
          }
        />
        {/* T-023 — WS connection lifecycle (silent until > 5s disconnect). */}
        <ConnectionBanner />
        {/* F-115 / T-014 — Mirlai OS update notification (ADR-003 Amendment §3). */}
        <UpdateBanner />
        {/* T-030 — Offline indicator. Stays mounted while `navigator.onLine`
            is false; reconnect clears it. We surface `lastOnlineAt` so the
            user knows how stale the cached view is. */}
        {offline && (
          <Banner
            kind="warning"
            title={t("pwa.offline_banner_title")}
            message={
              lastOnlineAt
                ? t("pwa.last_update_relative", {
                    when: new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }).format(
                      Math.round((lastOnlineAt - Date.now()) / 60_000),
                      "minute",
                    ),
                  })
                : t("pwa.offline_banner_body")
            }
          />
        )}
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
